/**
 * A satellite snapshot of a plot's boundary for the consent PDF (owner, 2026-09-28: "the
 * representative boundary in consent PDF, can we add snapshot of actual polygon?").
 *
 * The Esri World Imagery tiles around the plot (the same imagery the app's maps show) are drawn on a
 * canvas at the zoom that fits the boundary, and the boundary is drawn over them with its corners
 * numbered as the coordinate list numbers them, and a north arrow. Returns a JPEG data URI, or null
 * when the imagery cannot be fetched - the PDF then keeps its drawn sketch.
 */
const TILE = 256;
const TILE_URL = (z: number, x: number, y: number) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

const worldX = (lng: number, z: number) => ((lng + 180) / 360) * TILE * 2 ** z;
const worldY = (lat: number, z: number) => {
  const r = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * TILE * 2 ** z;
};

function loadTile(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

export async function boundarySnapshot(ring: Array<[number, number]> | null | undefined,
                                       width = 300, height = 220, maxCorners = 24): Promise<string | null> {
  const points = (ring ?? []).filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
  const corners = points.length > 1 && points[0]![0] === points.at(-1)![0] && points[0]![1] === points.at(-1)![1] ? points.slice(0, -1) : points;
  if (corners.length < 3 || typeof document === 'undefined') return null;
  const lngs = corners.map((point) => point[0]);
  const lats = corners.map((point) => point[1]);
  const [minX, maxX, minY, maxY] = [Math.min(...lngs), Math.max(...lngs), Math.min(...lats), Math.max(...lats)];

  // The highest zoom (up to 18) at which the boundary fills no more than 70% of the frame.
  let zoom = 18;
  while (zoom > 3 && (worldX(maxX, zoom) - worldX(minX, zoom) > width * 0.7 || worldY(minY, zoom) - worldY(maxY, zoom) > height * 0.7)) zoom -= 1;

  const scale = 2; // drawn at twice the size, for a sharp print
  const cx = (worldX(minX, zoom) + worldX(maxX, zoom)) / 2;
  const cy = (worldY(maxY, zoom) + worldY(minY, zoom)) / 2;
  const left = cx - width / 2;
  const top = cy - height / 2;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const g = canvas.getContext('2d');
  if (!g) return null;
  g.scale(scale, scale);
  g.fillStyle = '#e5e7eb';
  g.fillRect(0, 0, width, height);

  const tiles: Array<Promise<boolean>> = [];
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + width) / TILE); tx += 1) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty += 1) {
      tiles.push(loadTile(TILE_URL(zoom, tx, ty)).then((image) => {
        if (image) g.drawImage(image, tx * TILE - left, ty * TILE - top, TILE, TILE);
        return Boolean(image);
      }));
    }
  }
  const loaded = await Promise.all(tiles);
  if (!loaded.some(Boolean)) return null;

  const xy = corners.map(([lng, lat]) => [worldX(lng, zoom) - left, worldY(lat, zoom) - top] as const);
  g.beginPath();
  xy.forEach(([x, y], index) => (index === 0 ? g.moveTo(x, y) : g.lineTo(x, y)));
  g.closePath();
  g.fillStyle = 'rgba(250, 204, 21, 0.18)';
  g.fill();
  g.lineWidth = 2;
  g.strokeStyle = '#facc15';
  g.stroke();

  g.font = 'bold 9px sans-serif';
  xy.slice(0, maxCorners).forEach(([x, y], index) => {
    g.fillStyle = '#facc15';
    g.beginPath();
    g.arc(x, y, 2.6, 0, Math.PI * 2);
    g.fill();
    g.lineWidth = 2.5;
    g.strokeStyle = 'rgba(0,0,0,0.75)';
    g.strokeText(String(index + 1), x + 4, y - 3);
    g.fillStyle = '#ffffff';
    g.fillText(String(index + 1), x + 4, y - 3);
  });

  // North arrow, top right.
  g.fillStyle = 'rgba(255,255,255,0.85)';
  g.fillRect(width - 24, 4, 20, 26);
  g.fillStyle = '#111';
  g.beginPath();
  g.moveTo(width - 14, 8);
  g.lineTo(width - 19, 19);
  g.lineTo(width - 14, 16);
  g.lineTo(width - 9, 19);
  g.closePath();
  g.fill();
  g.font = 'bold 8px sans-serif';
  g.fillText('N', width - 17, 28);

  try {
    return canvas.toDataURL('image/jpeg', 0.86);
  } catch {
    return null; // a tile without CORS headers taints the canvas
  }
}
